import { colors } from "../theme/muiColors";
import dayjs from "dayjs";
import jwtDecode from "jwt-decode";
import { DateTime, IANAZone } from "luxon";
import { ResponseError, gateway, refreshGateway } from "../gateway";
import { gatewayEventScheduler } from "../gateways/gatewayEventScheduler";
import { clearModalDockStorage } from "./modalDockStorage";

export const IRoles = {
  Business: "business",
  Employee: "employee",
  SuperAdmin: "superadmin",
  Admin: "admin",
  Client: "client",
  VR: "virtualReceptionist",
  Manager: "accountManager",
  Other: "other",
};

export const ISignupRole = {
  EMPLOYEE: "Employee",
  CLIENT: "Client",
  ADMIN: "Admin",
  VR: "Virtual Receptionist",
  MANAGER: "Manager",
  BUSINESS: "Business",
};

export const EBenefitTypes = {
  FINDACLEANER: "findacleaner",
  SUPPORT: "support",
  AREASCOVERAGES: "areascoverages",
};

export const EConnectionStatus = {
  ACCEPTED: "accepted",
  DECLINED: "declined",
  PENDING: "pending",
  CANCELED: "canceled",
};

export const ESubscriptionStatus = {
  CANCELLED: "cancelled",
  COMPLETED: "completed",
  SUSPENDED: "suspended",
  ACTIVE: "active",
  FAILED: "failed",
};

export const EStaffTypes = {
  EMPLOYEE: "employee",
  CONTRACTOR: "contractor",
  SUPERVISOR: "supervisor",
};

export const EServiceType = {
  RESIDENTIAL: "residencial",
  COMMERCIAL: "commercial",
};

export const EJobType = {
  BASIC: "basic",
  ADVANCED: "advanced",
};

export const ESourceEntityType = {
  WORKFORCE: "workforce",
  BUSINESS: "business",
  CLIENT: "client",
};

export const EJobStatuses = {
  ASSIGNED: "assigned",
  PENDING: "pending",
  APPROVED: "approved",
  DECLINED: "declined",
  SCHEDULED: "scheduled",
  IN_PROGRESS: "inProgress",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

export const JobFrequencyList = [
  { value: "ONCE", label: "One Time" },
  // { value: "DAY", label: "Daily" },
  // { value: "WEEK", label: "Weekly" },
  // { value: "MONTH", label: "Montly" },
  // { value: "YEAR", label: "Yearly" },
];

export const StaffTypes = [
  { value: "employee", label: "Employee" },
  { value: "contractor", label: "Contractor" },
  { value: "supervisor", label: "Supervisor" },
];

// Residential calculator
export const interiorRooms = [
  { label: "Master Bedrooms", name: "masterBedroom" },
  { label: "Other Rooms", name: "otherRoom" },
];

export const bathrooms = [
  { label: "Master Bathrooms", name: "masterBathrooms" },
  { label: "Regular Bathrooms", name: "regularBathrooms" },
  { label: "Half Bathrooms", name: "halfBathrooms" },
];

export const otherAreas = [
  { label: "Kitchen", name: "kitchen" },
  { label: "Laundry Room", name: "laundryRoom" },
  { label: "Living Room", name: "livingRoom" },
  { label: "Stairs", name: "stairs" },
];

export const additionalRequests = [
  {
    label: "Fridge Interior Cleaning",
    name: "fridgeInteriorCleaning",
  },
  { label: "Fill and Run Dishwasher", name: "fillAndRunDishwasher" },
  { label: "Tidy Up", name: "tidyUp" },
  {
    label: "Window Interior Cleaning",
    name: "windowInteriorCleaning",
  },
  { label: "Oven Interior Cleaning", name: "ovenInteriorCleaning" },
  { label: "Hand Dish Washing", name: "handDishWashing" },
  { label: "Garage Cleaning", name: "garageCleaning" },
];

export const ECurrency = {
  USD: "USD",
  MXN: "MXN",
};

export const EClientPlatform = {
  WEB: "web",
  MOBILE: "mobile",
};

export const EBillingType = {
  PAYG: "PAYG",
  FIXED: "FIXED",
};

export const decodeToken = (token) => {
  if (!token || typeof token !== "string") return null;

  try {
    return jwtDecode(token);
  } catch (error) {
    console.warn("decodeToken: token inválido →", error.message);
    return null;
  }
};

export const milesToKm = (miles) => {
  return miles * 1.60934;
};

export const kmToMiles = (km) => {
  return km * 0.621371;
};

export const generateErrorCodeHandlerInBaseOfError = (error, errorName, errDescription) => {
  try {
    let e = null;
    if ((error.name === "AxiosError" && error?.response?.data?.status === 400) || error?.response?.data?.status === 500) {
      e = new ResponseError(errorName, errDescription);
    }
    return e ?? error;
  } catch (error) {
    console.error("Error when tried generateErrCode:", error);
    throw error;
  }
};

export const getMyServices = async (type, token, companyId = null) => {
  try {
    const companyIdStr = companyId === undefined || companyId === null || companyId === "" ? undefined : String(companyId);

    if (companyIdStr !== undefined && Number.isNaN(Number(companyIdStr))) {
      console.error("Invalid companyId in getMyServices:", companyId);
      throw new Error("Invalid companyId");
    }

    const path = companyIdStr ? `/workservices/self` : `/workservices/self`;

    const { data } = await gateway.get(path, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const list = data?.data ?? [];
    const wantResidential = String(type).toLowerCase().startsWith("resid");

    const filtered = list.filter((s) => {
      const t = (s?.workTypes?.type || "").toLowerCase();
      const isRes = t.startsWith("resid");
      const isCom = t.startsWith("commerc");
      return wantResidential ? isRes : isCom;
    });

    return filtered;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_SERVICES", "Error when tried to find your skills");
    throw e;
  }
};

export const getMyAvailability = async (token) => {
  try {
    const response = await gateway.get(`/availability/self`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.data;
    return data;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_SERVICES", "Error when tried to find your skills");
    throw e;
  }
};

export const processSchedule = (inputData) => {
  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const fullDayNames = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  };

  let output = [];

  days.forEach((day) => {
    let dayData = {
      AM: inputData["AM"][day] || false,
      Midday: inputData["Midday"][day] || false,
      PM: inputData["PM"][day] || false,
    };
    let dayEntry = {};
    dayEntry[fullDayNames[day]] = dayData;
    output.push(dayEntry);
  });

  return output;
};

export const getMyRequirements = async (token) => {
  try {
    const response = await gateway.get(`/workrequirements/self`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.data;

    return data;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_REQUIREMENTS", "Error when tried to find your skills");
    throw e;
  }
};

export const mapToCoverageModel = (data) => {
  let result = [];
  if (data && data.length > 0) {
    result = data.map((item) => {
      if (!isNaN(Number(item.lat)) && !isNaN(Number(item.lng))) {
        const { lat, lng, radius, ...restBody } = item;
        return {
          latitude: Number(lat),
          longitude: Number(lng),
          milesOfCoverageArea: kmToMiles(Number(radius)) || 16093.4,
          ...restBody,
        };
      }
    });
  }
  return result;
};

export const mapToCoverageClient = (data) => {
  let result = [];
  if (data && data.length > 0) {
    result = data.map((item) => {
      if (!isNaN(Number(item.latitude)) && !isNaN(Number(item.longitude))) {
        const { latitude, longitude, milesOfCoverageArea, ...restBody } = item;
        return {
          lat: Number(latitude),
          lng: Number(longitude),
          radius: Number(milesOfCoverageArea),
          ...restBody,
        };
      }
    });
  }
  console.log("mapToCoverageClient: ", result);
  return result;
};

export const getMyLocation = async (token) => {
  try {
    // throw new ResponseError("ERR_CONNECTION_REFUSED", "error throwed.");
    const res = await gateway.get("/coverageareas/self", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    let result = mapToCoverageClient(data);
    return result;
  } catch (error) {
    console.log("error when find coveragearea ", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_LOCATION", "It was not posible to find your location.");
    throw e;
  }
};

export const getMyNotifications = async (token, page = 1, pageSize = 20, where = {}) => {
  try {
    const params = {
      pageSize: pageSize,
      page: page,
      where: JSON.stringify(where),
    };

    const res = await gateway.get(`/notifications/self`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find your notifications ", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_NOTIFICATIONS", "It was not posible to find your notification.");
    throw e;
  }
};

export const markAsSeenAllMyNotifications = async (token, options = {}) => {
  try {
    const res = await gateway.put("/notifications/self/markasseenall", options?.companyId ? { companyId: options.companyId } : {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data.data;
  } catch (error) {
    console.error("Error marking notifications as seen:", error);

    const e = generateErrorCodeHandlerInBaseOfError(
      error,
      "ERR_CANNOT_MARK_NOTIFICATIONS_AS_SEEN",
      "It was not possible to mark your notifications as seen.",
    );

    throw e;
  }
};

export const getNotificationStats = async (token) => {
  try {
    const res = await gateway.get("/notifications/stats", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data.data;
  } catch (error) {
    console.error("Error when fetching notification stats:", error);

    const e = generateErrorCodeHandlerInBaseOfError(
      error,
      "ERR_CANNOT_FIND_NOTIFICATION_STATS",
      "It was not possible to find your notification stats.",
    );

    throw e;
  }
};

export const getMyNotificationsSettings = async (token) => {
  try {
    const res = await gateway.get(`/notifications/settings-self`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find your notifications settings ", error);

    const e = generateErrorCodeHandlerInBaseOfError(
      error,
      "ERR_CANNOT_FIND_NOTIFICATIONS_SETTINGS",
      "It was not posible to find your notification settings.",
    );
    throw e;
  }
};

export const updateMyNotificationsSettings = async (body, token) => {
  try {
    const res = await gateway.put(
      `/notifications/settings-self`,
      { ...body },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find your notifications settings ", error);

    const e = generateErrorCodeHandlerInBaseOfError(
      error,
      "ERR_CANNOT_FIND_NOTIFICATIONS_SETTINGS",
      "It was not posible to find your notification settings.",
    );
    throw e;
  }
};

export const updateMyNotificatioById = async (token, id, body) => {
  try {
    const res = await gateway.put(`/notifications/self/${id}`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when tried to update your notification", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_UPDATE_NOTIFICATION", "It was not posible to update your notification.");
    throw e;
  }
};

export const markAsReadAllMyNotifications = async (token, options = {}) => {
  try {
    const res = await gateway.put("/notifications/self/markasreadall", options?.companyId ? { companyId: options.companyId } : {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data.data;
  } catch (error) {
    console.error("Error marking notifications as read:", error);

    const e = generateErrorCodeHandlerInBaseOfError(
      error,
      "ERR_CANNOT_MARK_NOTIFICATIONS_AS_READ",
      "It was not possible to mark your notifications as read.",
    );

    throw e;
  }
};
export const createOrUpdateMyLocation = async (token, payload, userBenefitId) => {
  try {
    const endpoint = userBenefitId ? `/coverageareas/self/${userBenefitId}` : `/coverageareas/self`;

    const res = await gateway.put(
      endpoint,
      { coverageAreas: payload },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return res;
  } catch (error) {
    console.error("Error updating coverage area:", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_UPDATE_LOCATIONS", "It was not possible to update your location.");
    throw e;
  }
};

export const generateRandomPhoneNumber = () => {
  const randomDigit = () => Math.floor(Math.random() * 10);
  return `+${randomDigit()}${randomDigit()}-${randomDigit()}${randomDigit()}-${randomDigit()}${randomDigit()}-${randomDigit()}${randomDigit()}`;
};

export const getPlans = async (section = null) => {
  try {
    const res = await gateway.get(`/paypal/plans`, {
      params: {
        ...(section ? { "product.section": section } : {}),
      },
    });
    const data = res.data.data;
    return data?.items ?? [];
  } catch (error) {
    console.log("error when find plans ", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_PLANS", "It was not posible to find company plans.");

    throw e;
  }
};

export const getPlanById = async (paypalPlanId, isFreeTrial = false, isCustom = false, billingType = EBillingType.FIXED) => {
  try {
    const res = await gateway.get(`/paypal/plans/${paypalPlanId}`, {
      params: {
        isFreeTrial,
        isCustom,
        billingType,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find plan ", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_PLAN", "It was not posible to find company plan.");
    throw e;
  }
};

export const getPlanByCodeDiscount = async (promoCodeDiscount) => {
  try {
    const res = await gateway.get(`/paypal/plans-promo/${promoCodeDiscount}`);
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find plan with promo code discount ", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_PLAN", "It was not posible to find company plan.");
    throw e;
  }
};

export const createConnectSubscription = async (paypalSubscriptionId, backendSubscriptionId, paypalPlanId, subtotal, discount, total, token) => {
  try {
    //Create a subscription in our web but not in paypal side
    //Use this when paypal payment was well
    const res = await gateway.post(
      `/paypal/subscriptions-connect`,
      {
        paypalSubscriptionId: paypalSubscriptionId,
        subscriptionId: backendSubscriptionId,
        paypalPlanId,
        subtotal,
        discount,
        total,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find plan with promo code discount ", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_PLAN", "It was not posible to find company plan.");
    throw e;
  }
};

export const getMySubscriptionInfo = async (token) => {
  try {
    const res = await gateway.get(`/paypal/subscriptions-self`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find self subscription.", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_SUBSCRIPTION_SELF", "It was not posible to find your subscription.");
    throw e;
  }
};

export const cancelMySubscriptionInfo = async (token, subscriptionId) => {
  try {
    const res = await gateway.delete(`/paypal/subscriptions-self`, {
      data: {
        subscriptionId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find delete your subscription.", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_DELETE_SUBSCRIPTION_SELF", "It was not posible to delete your subscription.");
    throw e;
  }
};

export const getUsersSummary = async (type, page, pageSize, token, isDeleted = false) => {
  try {
    const params = {
      type,
      page,
      pageSize,
      isDeleted,
    };

    console.log(params);
    const res = await gateway.get(`/admin/summary`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find delete your subscription.", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_DELETE_SUBSCRIPTION_SELF", "It was not posible to delete your subscription.");
    throw e;
  }
};

export const getUsersQuickSummary = async (token) => {
  try {
    const res = await gateway.get(`/admin/summary-quick`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find delete your subscription.", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_GET_USER_SUMMARY", "It was not posible to get users summary.");
    throw e;
  }
};

export const deleteUsersByAdmin = async (userUids = [], token) => {
  try {
    const parseUsersUids = encodeURIComponent(userUids);

    const res = await gateway.delete(`/admin/delete-by-admin?uids=${parseUsersUids}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find delete your subscription.", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_DELETE_SUBSCRIPTION_SELF", "It was not posible to delete your subscription.");
    throw e;
  }
};

export const restoreUsersByAdmin = async (userUids = [], token) => {
  try {
    const parseUsersUids = encodeURIComponent(userUids);

    const res = await gateway.delete(`/admin/restore-by-admin?uids=${parseUsersUids}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when find delete your subscription.", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_DELETE_SUBSCRIPTION_SELF", "It was not posible to delete your subscription.");
    throw e;
  }
};

export const enterpriseContactInfo = async (name, title, company, email, phone, location, message, countryCode, token) => {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("title", title);
  formData.append("company", company);
  formData.append("email", email);
  formData.append("phone", phone);
  formData.append("location", location);
  formData.append("message", message);
  formData.append("countryCode", countryCode);
  try {
    const res = await gateway.post(
      `/paypal/plans/enterprise-contact-us`,
      { name, title, company, email, phone, location, message, countryCode },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const data = res.data.data;
    return data;
  } catch (error) {
    console.log("error when sending contact info", error);

    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_SEND_CONTACT_INFO", "It was not possible to send contact information.");
    throw e;
  }
};

export const setMetaDescription = () => {
  const language = navigator.language;

  if (language.startsWith("es")) {
    const description =
      "Potencia tu empresa de limpieza o consigue más trabajo con Connect a Cleaner. Conéctate sin esfuerzo con empresas de primer nivel y profesionales cualificados. ¡Únete ahora!";
    const metaDescriptionTag = document.querySelector('meta[name="description"]');
    metaDescriptionTag.setAttribute("content", description);
  }
};

export const getMyConnections = async ({ page = 1, pageSize = 5, token }) => {
  try {
    const query = `?page=${page}&pagesize=${pageSize}`;

    const res = await gateway.get(`/connect/self/${query}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data.data;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_SERVICES", "Error when trying to find your connections");
    throw e;
  }
};

export const deleteMyConnections = async (id, token) => {
  try {
    const res = await gateway.delete(`/connect/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.data;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_DELETE_CONNECTION", "Error when tried to delete the notification.");
    throw e;
  }
};

export const updateMyConnections = async (id, status, token) => {
  try {
    const res = await gateway.patch(
      `/connect/${id}`,
      {
        status,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return res.data.data;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_UPDATE_CONNECTION", "Error when tried to update the notification.");
    throw e;
  }
};

export const getMyCredentials = async (token) => {
  try {
    const res = await gateway.get(`/paypal/credentials`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.data;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(error, "ERR_CANNOT_FIND_SERVICES", "Error when tried to find your skills");
    throw e;
  }
};

export const storeMyCredentials = async (token, clientId, clientSecret) => {
  try {
    const res = await gateway.post(
      `/paypal/credentials`,
      { clientId, clientSecret },
      {
        headers: {
          Authorization: `${token}`,
        },
      },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const updateMyCredentials = async (token, clientId, clientSecret) => {
  try {
    const res = await gateway.patch(
      `/paypal/credentials`,
      { clientId, clientSecret },
      {
        headers: {
          Authorization: `${token}`,
        },
      },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const googleOauthLogin = async (credential, clientId, select_by) => {
  try {
    const res = await gateway.post(`/users/google-oauth`, {
      credential,
      clientId,
      select_by,
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const createAssistantBotSession = async (token) => {
  try {
    const res = await gateway.post(
      `/assistantbot/session`,
      {},
      {
        headers: {
          Authorization: `${token}`,
        },
      },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const sendAssistantBotMessage = async (token, message) => {
  try {
    const res = await gateway.post(
      `/assistantbot/message`,
      { message },
      {
        headers: {
          Authorization: `${token}`,
        },
      },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getBasePath = (roleType) => {
  // return roleType === "business" ? "/business" : "/workforce";
  switch (roleType) {
    case "business":
      return "/business";
    case "admin":
      return "/business";
    case "virtualReceptionist":
      return "/business";
    case "accountManager":
      return "/business";
    case "employee":
      return "/workforce";
    case "superadmin":
      return "/admin";
    case "other":
      return "/business";
    default:
      return "/signin";
  }
};

export const createClient = async (token, payload) => {
  try {
    const res = await gateway.post(`/clients`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const deleteClientApi = async (token, id) => {
  try {
    const res = await gateway.delete(`/clients?uids=${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const addressByGeocode = ({ lat, lng }) => {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      reject("Google Maps API is not loaded");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat, lng };

    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK") {
        console.log(results);
        if (results[0]) {
          resolve(results[0].formatted_address);
        } else {
          reject("No se encontraron resultados");
        }
      } else {
        reject("Geocoder falló: " + status);
      }
    });
  });
};

export const coverageAreaByGeocode = async ({ lat, lng }) => {
  if (!window.google || !window.google.maps) {
    throw new Error("Google Maps API is not loaded");
  }

  const geocoder = new window.google.maps.Geocoder();
  const latlng = { lat, lng };

  const results = await new Promise((resolve, reject) => {
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        resolve(results);
      } else {
        reject(new Error("Geocoder falló: " + status));
      }
    });
  });

  const result = results[0];
  const addressComponents = result.address_components || [];

  let country = "";
  let state = "";
  let city = "";
  let zipCode = "";

  addressComponents.forEach((component) => {
    if (component.types.includes("country")) {
      country = component.long_name;
    }
    if (component.types.includes("administrative_area_level_1")) {
      state = component.long_name;
    }
    if (component.types.includes("locality")) {
      city = component.long_name;
    }
    if (component.types.includes("postal_code")) {
      zipCode = component.long_name;
    }
  });

  return {
    state,
    city,
    country,
    zipCode,
    address: result.formatted_address,
    latitude: lat,
    longitude: lng,
    milesOfCoverageArea: 0,
  };
};

export const getWorkServiceByType = async (type) => {
  try {
    const response = await gateway.get(`/workservices?type=${type}`);
    const data = response.data.data.items;
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getWorkServicesByTypeWithCompany = async (token, companyId, type) => {
  try {
    if (!companyId || isNaN(companyId)) {
      console.error("Invalid companyId in getWorkServicesByTypeWithCompany:", companyId);
      throw new Error("Invalid companyId");
    }
    const response = await gateway.get(`/workservices/self/${companyId}?type=${type}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data?.data ?? [];
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const workTypeServiceToSwitchElement = (workTypeService) => {
  return {
    id: workTypeService?.id,
    name: workTypeService?.workServices?.name,
    workServiceId: workTypeService?.workServices?.id,
    workService: workTypeService?.workServices,
    workTypeId: workTypeService?.workTypes?.id,
    workType: workTypeService?.workTypes,
    checked: workTypeService?.checked,
  };
};

export const dateIsoStringToTimeAmpm = (dateIsoString) => {
  return dayjs(dateIsoString).format("h:mm A");
};

export const dateIsoStrJoinTimeIsoStr = (dateIsoStr, timeIsoStr) => {
  const dateIsoStrSection = dateIsoStr.split("T")[0];
  const timeIsoStrSection = timeIsoStr.split("T")[1];
  return `${dateIsoStrSection}T${timeIsoStrSection}`;
};

export const createJob = async (token, payload) => {
  try {
    const res = await gateway.post(`/jobs/self`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res;
  } catch (error) {
    throw error;
  }
};

export const getMyJobs = async ({ token, jobSchema, page = 1, pageSize = 10 }) => {
  try {
    const res = await gateway.get(`/jobs/self?page=${page}&pageSize=${pageSize}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getMyJobsForDate = async ({ token, startDate, endDate, pageSize = 1000 }) => {
  console.log(startDate, endDate);

  try {
    const res = await gateway.get(`/jobs/self?startDate=${startDate}&endDate=${endDate}&pageSize=${pageSize}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getMyEmployeeJobs = async ({ token, userUid, page = 1, pageSize = 100 }) => {
  console.log(userUid);

  try {
    const res = await gateway.get(`/jobs/self/employee/${userUid}?page=${page}&pageSize=${pageSize}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const saveMyServices = async (tokenLocal, payload) => {
  try {
    const response = await gateway.put(
      `/workservices/self`,
      {
        services: payload,
      },
      {
        headers: {
          Authorization: `Bearer ${tokenLocal}`,
        },
      },
    );
    const data = response.data.data;
    const { residential, commercial } = data;
    return data;
  } catch (error) {
    throw error;
  }
};

export const widgetLargeHeight = {
  md: "400px",
};

export const widgetSmallHeight = {
  md: "470px",
};
export const calculateBasicCalculator = async (data) => {
  try {
    const { typeOfServiceId, billingHourlyRate, employeePayRate, payrollTax, workersComp, benefits, otherCosts } = data;

    const res = await gateway.post(`/calculator/simple`, {
      typeOfServiceId,
      billingHourlyRate,
      employeePayRate,
      payrollTax,
      workersComp,
      benefits,
      otherCosts,
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getJobsById = async ({ token, id }) => {
  try {
    const res = await gateway.get(`/jobs/self/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const patchQuotesById = async (token, id, patchData) => {
  try {
    const isFormData = patchData instanceof FormData;

    const res = await gateway.patch(`/quotes/self/${id}`, patchData, {
      headers: {
        Authorization: `${token}`,
        ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
      },
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const patchJobsById = async (token, id, patchData) => {
  try {
    const res = await gateway.patch(`/jobs/self/${id}`, patchData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const patchStaffType = async ({ token, uid, staffType }) => {
  try {
    const res = await gateway.patch(
      `/workforce/manage-type/${uid}`,
      { type: staffType },
      {
        headers: {
          Authorization: `${token}`,
        },
      },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getAllJobs = async ({ token }) => {
  try {
    const res = await gateway.get(`/jobs/self`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data || res.data.data;
  } catch (error) {
    throw error;
  }
};

export const getAllJobsEvents = async ({ token, startDate, endDate, skipEndDate, page = 1, pageSize = 9999999 }) => {
  try {
    let url = `/jobs/events/self?pageSize=${pageSize}&page=${page}`;

    if (startDate) {
      url += `&startDate=${startDate}`;
    }

    if (endDate && !skipEndDate) {
      url += `&endDate=${endDate}`;
    }

    const res = await gateway.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data || res.data.data;
  } catch (error) {
    throw error;
  }
};

export const getJobsByDateandStatus = async ({ token, date, status }) => {
  try {
    const res = await gateway.get(`/jobs/self/?status=${status}&date=${date}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getSuppliesByProviders = async ({ token, pageSize = "100", total = "10" }) => {
  try {
    const res = await gateway.get(`/supplies/providers/self?pageSize=${pageSize}&totalPages=${total}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const patchSuppliesByProviders = async (token, supplieId, price) => {
  try {
    const res = await gateway.patch(
      `/supplies/providers/self/${supplieId}`,
      { price: price },
      {
        headers: {
          Authorization: `${token}`,
        },
      },
    );
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const getWorkForces = async (token) => {
  const { data } = await gateway.get(`/workforce/self`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data.data;
};
export const formatStatus = (status) => {
  return status.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (s) => s.toUpperCase());
};

export const getTrackerDevices = async (token, deviceTrackerUid, { startDateIso, endDateIso, pageSize = 50 } = {}) => {
  try {
    const buildParams = (page) => {
      const params = new URLSearchParams();
      if (startDateIso) params.append("filter[createdAt][$gte]", startDateIso);
      if (endDateIso) params.append("filter[createdAt][$lte]", endDateIso);
      params.append("page", page);
      params.append("pageSize", pageSize);
      return params.toString();
    };

    // 1. Hacer la primera petición para descubrir totalPages y recolectar la primera tanda de datos
    const firstRes = await gateway.get(`/device/self/${deviceTrackerUid}/records?${buildParams(1)}`, { headers: { Authorization: `${token}` } });

    const normalizePayload = (payload) => {
      // Normalizar la respuesta del backend.
      const data = payload?.data ?? payload;
      let items = [];
      if (Array.isArray(data)) items = data;
      else if (data && typeof data === "object") {
        if (Array.isArray(data.items)) items = data.items;
        else if (Array.isArray(data.records)) items = data.records;
        else if (Array.isArray(data.trackers)) items = data.trackers;
        else if (Array.isArray(data.data)) items = data.data;
        else items = [data];
      }
      const pages = data.totalPages ?? 1;
      return { items, pages };
    };

    const { items: firstItems, pages: totalPages } = normalizePayload(firstRes.data);

    // Si solo hay una página, retornar resultados inmediatamente
    if (totalPages === 1) return firstItems;

    // 2. Crear array de promesas para las páginas restantes y ejecutarlas en paralelo
    const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2); // [2..totalPages]

    const fetchPromises = pageNumbers.map((p) =>
      gateway
        .get(`/device/self/${deviceTrackerUid}/records?${buildParams(p)}`, { headers: { Authorization: `${token}` } })
        .then((res) => normalizePayload(res.data).items)
        .catch((err) => {
          console.error(`Error fetching page ${p}:`, err);
          return []; // continuar sin interrumpir las demás descargas
        }),
    );

    const remainingPagesItems = await Promise.all(fetchPromises);

    return [...firstItems, ...remainingPagesItems.flat()];
  } catch (error) {
    console.error("Error fetching tracker devices:", error);
    return [];
  }
};

export const getMyDeviceTrackers = async (token, pageSize = 2000) => {
  try {
    let page = 1;
    let totalPages = 1;
    const allItems = [];

    do {
      const res = await gateway.get(`/device/self?filter[status][$eq]=linked&page=${page}&pageSize=${pageSize}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const payload = res.data?.data ?? res.data;
      let items = [];
      if (Array.isArray(payload.items)) items = payload.items;
      else if (Array.isArray(payload.data)) items = payload.data;
      else if (Array.isArray(payload)) items = payload;

      allItems.push(...items);
      totalPages = payload.totalPages ?? totalPages;
      page += 1;
    } while (page <= totalPages);

    return allItems;
  } catch (err) {
    console.error("Error fetching device trackers:", err);
    return [];
  }
};

export const createDeviceTracker = async (token, { type = "tracker", workforceUid }) => {
  try {
    const res = await gateway.post(`/device/self`, { type, workforceUid }, { headers: { Authorization: `${token}` } });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const deleteDeviceTracker = async (token, deviceTrackerUid) => {
  try {
    const res = await gateway.delete(`/device/self/${deviceTrackerUid}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const linkDeviceTracker = async (token, deviceTrackerUid, physicalDeviceUid) => {
  try {
    const res = await gateway.post(`/device/${deviceTrackerUid}/link`, { physicalDeviceUid }, { headers: { Authorization: `${token}` } });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getEmployeeDevice = async (token, employeeUid) => {
  try {
    const res = await gateway.get(`/device/self/employee/${employeeUid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getWorkforceDevice = async (token, workforceUid) => {
  try {
    const res = await gateway.get(`/device/self/workforce/${workforceUid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getLastRecord = async (token, deviceTrackerUid) => {
  try {
    const res = await gateway.get(`/device/self/${deviceTrackerUid}/records?pageSize=1&page=1`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data.items;
  } catch (error) {
    throw error;
  }
};

export const getContactsWithDevice = async (token) => {
  try {
    const res = await gateway.get(`/connect/my-workforce`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

// Function to get the workforce UID for the current employee user
export const getMyWorkforceUid = async (token, companyId) => {
  try {
    const currentUser = decodeToken(token);
    const userUid = currentUser?.uid || currentUser?.id;

    if (!userUid || !companyId) {
      throw new Error("Missing user UID or company ID");
    }

    // Get all workforce for the company
    const workforceData = await getWorkForces(token, companyId);

    // Find the workforce record that matches the current user's UID
    const myWorkforceRecord = workforceData?.find((workforce) => workforce.userUid === userUid);

    if (!myWorkforceRecord) {
      console.warn("No workforce record found for current user:", userUid);
      return null;
    }

    return myWorkforceRecord.uid;
  } catch (error) {
    console.error("Error getting workforce UID:", error);
    throw error;
  }
};

export const patchDeviceTracker = async (token, deviceTrackerUid, payload) => {
  try {
    // e.g. : {{url}}/{{api_version}}/device/self/94f6738f-9195-45c8-8459-3e54ac4547b4
    // e.g. : {{url}}/{{api_version}}/device/self/94f6738f-9195-45c8-8459-3e54ac4547b4/link
    // e.g. : {{url}}/{{api_version}}/device/self/94f6738f-9195-45c8-8459-3e54ac4547b4/unlink
    const res = await gateway.patch(`/device/self/${deviceTrackerUid}`, payload, { headers: { Authorization: `${token}` } });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const unLinkDeviceTracker = async (token, employeeUid, deviceTrackerUid) => {
  try {
    const res = await gateway.delete(`/device/self/${deviceTrackerUid}`, { headers: { Authorization: `${token}` } });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const statusConfig = {
  forRevision: { color: colors.grey[600], bg: colors.grey[50] },
  approved: { color: colors.amber[700], bg: colors.amber[50] },
  scheduled: { color: colors.purple[700], bg: colors.purple[50] },
  inProgress: { color: colors.blue[700], bg: colors.blue[50] },
  completed: { color: colors.green[700], bg: colors.green[50] },
  support: { color: colors.cyan[700], bg: colors.cyan[50] },
  cancelled: { color: colors.red[700], bg: colors.red[50] },
};

export const distanceForMileage = async (token, payload) => {
  try {
    const res = await gateway.post(`/coverageareas/calculate-distance`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const quoteCalculator = async (token, payload) => {
  try {
    const res = await gateway.post(`/quotes/?save=false`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

// Function to determine sender entity type based on their role
export const determineSenderEntityType = (currentUserRole) => {
  const currentUserRoleType = currentUserRole?.type?.toLowerCase();

  // Direct mapping from user role to entity type
  switch (currentUserRoleType) {
    case "business":
      return ESourceEntityType.BUSINESS;
    case "employee":
    case "contractor":
    case "supervisor":
      return ESourceEntityType.WORKFORCE;
    case "client":
      return ESourceEntityType.CLIENT;
    default:
      // Default to workforce
      return ESourceEntityType.WORKFORCE;
  }
};

// Function to determine entity type based on context
export const determineReceiverEntityType = (currentUserRole, receiverInfo = {}) => {
  // If type is already explicitly specified, use it
  if (receiverInfo.type && Object.values(ESourceEntityType).includes(receiverInfo.type)) {
    return receiverInfo.type;
  }

  // Mapping based on current user role and context
  const currentUserRoleType = currentUserRole?.type?.toLowerCase();

  // If current user is business, generally sends messages to workforce
  if (currentUserRoleType === "business") {
    return ESourceEntityType.WORKFORCE;
  }

  // If current user is employee, can send to business or workforce
  if (currentUserRoleType === "employee") {
    // If receiver has business information, it's business
    if (receiverInfo.businessName || receiverInfo.companyId) {
      return ESourceEntityType.BUSINESS;
    }
    // By default, employee to employee is workforce
    return ESourceEntityType.WORKFORCE;
  }

  // If current user is client, sends to business
  if (currentUserRoleType === "client") {
    return ESourceEntityType.BUSINESS;
  }

  // Default to workforce (employees)
  return ESourceEntityType.WORKFORCE;
};

export const createAMessage = async (token, { file, name, receiver, companyId, plainText, type, currentUserRole, receiverInfo, sender }) => {
  try {
    console.log("createAMessage called with:", {
      hasFile: !!file,
      name,
      receiver,
      companyId,
      hasPlainText: !!plainText,
      type,
      currentUserRole,
      receiverInfo,
      sender,
    });

    const formData = new FormData();

    if (plainText) {
      formData.append("plainText", plainText);
    } else if (file) {
      formData.append("file", file);
    }

    formData.append("name", name);

    // Handle receiver as object or string for backward compatibility
    let receiverObj;
    if (typeof receiver === "object" && receiver.uid && receiver.type) {
      // Already an object with correct structure
      receiverObj = receiver;
    } else if (typeof receiver === "string" && type) {
      // It's a string, create object with provided type
      receiverObj = { uid: receiver, type: type };
    } else if (typeof receiver === "object" && receiver.uid && type) {
      // It's an object but without type, use provided type
      receiverObj = { uid: receiver.uid, type: type };
    } else if (typeof receiver === "string" || (typeof receiver === "object" && receiver.uid)) {
      // Determine type automatically based on context
      const uid = typeof receiver === "string" ? receiver : receiver.uid;
      const determinedType = determineReceiverEntityType(currentUserRole, receiverInfo);
      receiverObj = { uid, type: determinedType };
    } else {
      throw new Error("receiver must be an object with {uid, type} or a string with provided type");
    }

    // Handle sender - must be provided or determined automatically
    let senderObj;
    if (sender && typeof sender === "object" && sender.uid && sender.type) {
      // Sender already provided with correct structure
      senderObj = sender;
    } else if (currentUserRole) {
      // Determine sender based on current user
      const currentUser = decodeToken(token);
      const senderType = determineSenderEntityType(currentUserRole);
      let senderUid = currentUser?.uid || currentUser?.id;

      if (!senderUid) {
        console.error("Could not get current user UID:", currentUser);
        throw new Error("Could not get current user UID from token");
      }

      // If the user is an employee, use their workforce UID instead of user UID
      const currentUserRoleType = currentUserRole?.type?.toLowerCase();
      if (currentUserRoleType === "employee" && companyId) {
        try {
          const workforceUid = await getMyWorkforceUid(token, companyId);
          if (workforceUid) {
            senderUid = workforceUid;
            console.log("Using workforce UID for employee sender:", workforceUid);
          } else {
            console.warn("Could not get workforce UID for employee, using user UID as fallback");
          }
        } catch (error) {
          console.error("Error getting workforce UID, using user UID as fallback:", error);
        }
      }

      senderObj = {
        uid: senderUid,
        type: senderType,
      };

      console.log("Sender determined automatically:", senderObj);
    } else {
      throw new Error("Cannot determine sender. Provide sender or currentUserRole");
    }

    formData.append("receiver", JSON.stringify(receiverObj));
    formData.append("sender", JSON.stringify(senderObj));
    formData.append("companyId", companyId);

    console.log("Payload sent to backend:", {
      receiver: receiverObj,
      sender: senderObj,
      companyId,
      name,
    });

    const res = await gateway.post(`/messages`, formData, {
      headers: {
        Authorization: `${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getMyMessages = async (token, { page = 1, pageSize = 50, companyId, order = "DESC", receiverUid }) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    params.append("companyId", companyId.toString());
    params.append("order", order);

    if (receiverUid) {
      params.append("receiverUid", receiverUid);
    }

    const res = await gateway.get(`/messages?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getMessagesByCompanyUid = async (token, companyUid) => {
  try {
    const res = await gateway.get(`/messages?companyUid=${companyUid}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

//Para poner los modales de MUI por arriba de todo MenuProps={menuPropsModal}
export const menuPropsModal = {
  container: () => {
    let container = document.getElementById("modal-select-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "modal-select-container";
      container.style.zIndex = "99999999";
      container.style.position = "relative";
      document.body.appendChild(container);
    }
    return container;
  },
  PaperProps: {
    style: {
      zIndex: 99999999,
      maxHeight: 300,
    },
  },
};

export const amazonConnectPostCredentials = async (token, payload) => {
  try {
    const res = await gateway.post(`/credentials`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const amazonConnectGetCredentials = async (token, companyId) => {
  try {
    const res = await gateway.get(`/credentials?companyId=${companyId}&type=aws_connect`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const approvedOrDeclined = async (token, job, status) => {
  try {
    const res = await gateway.post(
      `/jobs/self/${job}/respond`,
      { status: status },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getPaypalOnboarding = async (token) => {
  try {
    const res = await gateway.get(`/paypal/e-commerce/onboarding`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const deleteProvider = async (token, providerId) => {
  try {
    const res = await gateway.delete(`/providers/self/${providerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getPaypalECommerceSelf = async (token) => {
  try {
    const res = await gateway.get(`/paypal/e-commerce/self`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    const e = generateErrorCodeHandlerInBaseOfError(
      error,
      "ERR_CANNOT_GET_PAYPAL_ECOMMERCE_SELF",
      "No fue posible obtener la información de PayPal e-commerce.",
    );
    throw e;
  }
};

export const putConfigurationService = async (token, payload) => {
  try {
    const res = await gateway.put(`/workservices/self/prices`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getConfigurationService = async (token, companyId, serviceId) => {
  try {
    if (!companyId || isNaN(companyId) || !serviceId || isNaN(serviceId)) {
      console.error("Invalid companyId or serviceId in getConfigurationService:", companyId, serviceId);
      throw new Error("Invalid companyId or serviceId");
    }
    const res = await gateway.get(`/workservices/self/prices/${companyId}/${serviceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const generateContract = async (contractUid) => {
  try {
    const res = await gateway.get(`/docusign/download/${contractUid}`, {
      responseType: "arraybuffer",
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const handleRefreshToken = async () => {
  const refreshToken = sessionStorage.getItem("refresh_token");

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await refreshGateway.post(
      "/tokens/refresh",
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      },
    );
    const { token, refresh_token } = response.data.data;
    sessionStorage.setItem("token", token);
    sessionStorage.setItem("refresh_token", refresh_token.token);
    return token;
  } catch (error) {
    console.error("Token refresh failed:", error);
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("refresh_token");
    clearModalDockStorage();
    throw error;
  }
};

export const createEvent = async (payload) => {
  try {
    const res = await gatewayEventScheduler.post(`/event`, payload);
    return res;
  } catch (error) {
    throw error;
  }
};

export const buildEventPayload = (eventConfig, timeItem, jobId) => {
  try {
    const { emitter, mode, duration, frequency, typeName, typeUid, endDate, startDate, name } = eventConfig;

    const isBuffered = typeof timeItem === "object";
    const at = isBuffered ? timeItem.start : timeItem;

    const meta = {
      webhookUrl: emitter?.url,
      payload: {
        action: "ASSIGN_EVENT_TO_JOB",
        entityId: jobId,
      },
    };

    if (mode === "REPETITIVE") {
      return {
        mode,
        status: "PENDING",
        emitter,
        endDate,
        typeName,
        typeUid,
        duration: parseInt(frequency?.duration ?? duration ?? 0, 10),

        frequency: {
          at,
          each: 1,
          type: frequency?.type ?? "weekly",
          interval: frequency?.interval ?? [],
          exclusionRules: frequency?.exclusionRules ?? [],
        },

        meta,
      };
    }

    if (mode === "SINGLE_EXECUTION") {
      const base = new Date(startDate);
      const [hour, minute] = at.split(":").map(Number);
      base.setHours(hour, minute, 0, 0);

      return {
        mode,
        name: name ?? "One-Time Event",
        startDate: base.toISOString(),
        typeUid,
        typeName,
        emitter,
        status: "PENDING",
        duration,
        endDate,
        meta,
      };
    }

    throw new Error(`Unsupported mode: ${mode}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const to12h = (time) => DateTime.fromFormat(time, "HH:mm").toFormat("hh:mm a");
export const to24h = (time) => DateTime.fromFormat(time, "hh:mm a").toFormat("HH:mm");

export const generate12HourOptions = (minuteStep = 10) => {
  const options = [];
  const start = DateTime.fromFormat("12:00 AM", "hh:mm a");
  const stepsPerDay = Math.floor((24 * 60) / minuteStep);

  for (let i = 0; i < stepsPerDay; i++) {
    options.push(start.plus({ minutes: i * minuteStep }).toFormat("hh:mm a"));
  }

  return options;
};

export const to12hDisplay = (value) => {
  if (!value) return value;

  if (value.includes("-")) {
    const [start, end] = value.split("-").map((v) => v.trim());

    const start12 = DateTime.fromFormat(start, "HH:mm").toFormat("hh:mm a");
    const end12 = DateTime.fromFormat(end, "HH:mm").toFormat("hh:mm a");

    return `${start12} - ${end12}`;
  }

  return DateTime.fromFormat(value.trim(), "HH:mm").toFormat("hh:mm a");
};

export const formatIsoDateTime = (isoString) => {
  if (!isoString) return "";

  const dt = DateTime.fromISO(isoString);

  if (!dt.isValid) return isoString;

  const date = dt.toFormat("MMM dd, yyyy");
  const time = dt.toFormat("hh:mm a");

  return `${date} ${time}`;
};
export const formatEventDisplay = (value) => {
  if (!value) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!item.start || !item.end) return "";
        const start12 = DateTime.fromFormat(item.start, "HH:mm").toFormat("hh:mm a");
        const end12 = DateTime.fromFormat(item.end, "HH:mm").toFormat("hh:mm a");
        return `${start12} - ${end12}`;
      })
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object" && value.date && value.start) {
    const { date, start, end } = value;
    const base = DateTime.fromISO(date);

    const startDt = base.set({
      hour: Number(start.split(":")[0]),
      minute: Number(start.split(":")[1]),
    });

    const endDt = end
      ? base.set({
          hour: Number(end.split(":")[0]),
          minute: Number(end.split(":")[1]),
        })
      : null;

    const dateDisplay = base.toFormat("MMM dd, yyyy");
    const startDisplay = startDt.toFormat("hh:mm a");
    const endDisplay = endDt ? endDt.toFormat("hh:mm a") : "";

    return endDisplay ? `${dateDisplay}  ${startDisplay} - ${endDisplay}` : `${dateDisplay}  ${startDisplay}`;
  }

  if (typeof value === "string" && value.includes("-") && !value.includes("T")) {
    const [start, end] = value.split("-").map((v) => v.trim());
    const start12 = DateTime.fromFormat(start, "HH:mm").toFormat("hh:mm a");
    const end12 = DateTime.fromFormat(end, "HH:mm").toFormat("hh:mm a");
    return `${start12} - ${end12}`;
  }

  if (typeof value === "string" && value.includes("T")) {
    const dt = DateTime.fromISO(value);
    if (!dt.isValid) return value;
    const date = dt.toFormat("MMM dd, yyyy");
    const time = dt.toFormat("hh:mm a");
    return `${date} ${time}`;
  }

  return value;
};

export const formatUSPhone = (value) => {
  if (!value) return null;

  let digits = value.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  if (digits.length !== 10) return value;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export function formatHHMM(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const formatDate = (dateInput, timeZone = "UTC", format = "hh:mm a, MMMM d, yyyy") => {
  const safeZone = IANAZone.isValidZone(timeZone) ? timeZone : "UTC";
  let date;
  if (dateInput instanceof Date) {
    date = DateTime.fromJSDate(dateInput).setZone(safeZone);
  } else {
    date = DateTime.fromISO(dateInput, { zone: "utc" }).setZone(safeZone);
  }

  if (!date.isValid) return "";

  return date.toFormat(format);
};

export const invalidateContactsAndClients = async (queryClient) => {
  if (queryClient && typeof queryClient.invalidateQueries === "function") {
    // Keep SMS/call UIs from showing deleted contact names via resolver caches.
    if (typeof queryClient.removeQueries === "function") {
      queryClient.removeQueries({ queryKey: ["sms", "contacts", "bulk-resolve"] });
      queryClient.removeQueries({ queryKey: ["contacts", "resolve-bulk"] });
      queryClient.removeQueries({ queryKey: ["caller-contacts-self"] });
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["clients"] }),
      queryClient.invalidateQueries({ queryKey: ["contacts"] }),
      queryClient.invalidateQueries({ queryKey: ["sms", "contacts"], refetchType: "all" }),
      queryClient.invalidateQueries({ queryKey: ["call-history"], refetchType: "all" }),
    ]);
  }
};

export const invalidateContactsAndWorkforces = async (queryClient) => {
  if (queryClient && typeof queryClient.invalidateQueries === "function") {
    if (typeof queryClient.removeQueries === "function") {
      queryClient.removeQueries({ queryKey: ["sms", "contacts", "bulk-resolve"] });
      queryClient.removeQueries({ queryKey: ["contacts", "resolve-bulk"] });
      queryClient.removeQueries({ queryKey: ["caller-contacts-self"] });
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["contacts"] }),
      queryClient.invalidateQueries({ queryKey: ["sms", "contacts"], refetchType: "all" }),
      queryClient.invalidateQueries({ queryKey: ["call-history"], refetchType: "all" }),
    ]);
  }
};

export const invalidateContacts = async (queryClient) => {
  if (queryClient && typeof queryClient.invalidateQueries === "function") {
    if (typeof queryClient.removeQueries === "function") {
      queryClient.removeQueries({ queryKey: ["sms", "contacts", "bulk-resolve"] });
      queryClient.removeQueries({ queryKey: ["contacts", "resolve-bulk"] });
      queryClient.removeQueries({ queryKey: ["caller-contacts-self"] });
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["vendors"] }),
      queryClient.invalidateQueries({ queryKey: ["contacts"] }),
      queryClient.invalidateQueries({ queryKey: ["sms", "contacts"], refetchType: "all" }),
      queryClient.invalidateQueries({ queryKey: ["call-history"], refetchType: "all" }),
    ]);
  }
};

export const closeContactModalKeys = (closeByKey) => {
  if (typeof closeByKey === "function") {
    closeByKey("Virtual-Receptionist-Contact-Details-Modal");
    closeByKey("Dashboard-ClientAdd-Business");
    closeByKey("Dashboard-Clients-Business");
  }
};

export const getRecordEmail = (client) => {
  if (!client) return "";

  const emailsProp = client.emails;
  if (emailsProp) {
    let emails = emailsProp;
    if (typeof emailsProp === "string") {
      try {
        emails = JSON.parse(emailsProp);
      } catch (e) {
        if (emailsProp.includes("@")) {
          return emailsProp;
        }
      }
    }

    if (Array.isArray(emails) && emails.length > 0) {
      const defaultEmailObj = emails.find((item) => item && typeof item === "object" && item.isDefault === true);
      if (defaultEmailObj) {
        if (typeof defaultEmailObj.email === "string") return defaultEmailObj.email;
        if (typeof defaultEmailObj.value === "string") return defaultEmailObj.value;
      }

      const firstItem = emails[0];
      if (firstItem) {
        if (typeof firstItem === "string") {
          return firstItem;
        }
        if (typeof firstItem === "object") {
          if (typeof firstItem.email === "string") return firstItem.email;
          if (typeof firstItem.value === "string") return firstItem.value;
        }
      }
    }
  }
  return client.email || "";
};
export const getClientEmail = getRecordEmail;

export const getRecordPhone = (client, options = {}) => {
  const formatted = typeof options === "boolean" ? options : !!options?.formatted;

  if (!client) return "";

  let rawPhone = "";

  const phonesProp = client.phoneNumbers || client.phones;
  if (phonesProp) {
    let phones = phonesProp;
    if (typeof phonesProp === "string") {
      try {
        phones = JSON.parse(phonesProp);
      } catch (e) {}
    }

    if (Array.isArray(phones) && phones.length > 0) {
      const defaultPhoneObj = phones.find((item) => item && typeof item === "object" && item.isDefault === true);
      if (defaultPhoneObj) {
        if (typeof defaultPhoneObj.phoneNumber === "string") {
          rawPhone = defaultPhoneObj.phoneNumber;
        } else {
          const dial = defaultPhoneObj.dialCode || "";
          const ph = defaultPhoneObj.phone || "";
          rawPhone = `${dial} ${ph}`.trim();
        }
      } else {
        const firstItem = phones[0];
        if (firstItem) {
          if (typeof firstItem === "string") {
            rawPhone = firstItem;
          } else if (typeof firstItem === "object") {
            if (typeof firstItem.phoneNumber === "string") {
              rawPhone = firstItem.phoneNumber;
            } else {
              const dial = firstItem.dialCode || "";
              const ph = firstItem.phone || "";
              rawPhone = `${dial} ${ph}`.trim();
            }
          }
        }
      }
    }
  }

  if (!rawPhone && client.phone) {
    const dial = client.dialCode || "";
    const ph = client.phone || "";
    rawPhone = `${dial} ${ph}`.trim();
  }

  if (formatted && rawPhone) {
    return formatUSPhone(rawPhone) || rawPhone;
  }

  return rawPhone;
};
export const getClientPhone = getRecordPhone;

export const getWorkforceByUid = async (token, uid) => {
  const { data } = await gateway.get(`/workforce/${uid}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data.data;
};

export const formatPhoneNumbers = (phones) => {
  return Array.isArray(phones)
    ? phones
        .map((p, index) => {
          const dialCode = p.dialCode || "+1";
          const rawPhone = p.phone || "";
          const cleanPhone = typeof rawPhone === "string" ? rawPhone.replace(/\D/g, "") : String(rawPhone || "").replace(/\D/g, "");
          return {
            phone: cleanPhone,
            dialCode: dialCode,
            isDefault: p.isDefault !== undefined ? !!p.isDefault : index === 0,
            countryCode: p.countryCode || "US",
            phoneNumber: `${dialCode}${cleanPhone}`,
          };
        })
        .filter((p) => p.phone)
    : [];
};

export const formatEmails = (emails) => {
  return Array.isArray(emails)
    ? emails
        .map((e, index) => {
          const emailVal = typeof e === "string" ? e : e.value || e.email || "";
          return {
            tag: e.tag || "other",
            email: emailVal,
            isDefault: e.isDefault !== undefined ? !!e.isDefault : index === 0,
          };
        })
        .filter((e) => e.email)
    : [];
};
